import { Link, useLocation, useNavigate } from "react-router-dom";
import React, { useState, useRef, useEffect } from "react";
import AudioPlayer from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";
import BackArrow from "../assets/images/back-arrow.svg";
import LunghImg from "../assets/images/lung-obstruction.png";
import VoiceImg from "../assets/images/Sneeze-icon.png";
import Failure from "../assets/images/failureNew.webp";
import { ReactComponent as GlowingHeart } from "../assets/icons/GlowingHeart.svg";
import { ReactComponent as Loader1 } from "../assets/icons/Loader.svg";
import RecordRTC from "recordrtc";
import { api_key, authEmployeeID, sdkURL } from "../utils/constants";
import { base64ToBlob } from "../utils/b64toBlob";
import LoadingScreen from "../components/LoadingScreen";

const MessageList = [
  "Hold your phone close to your mouth.",
  "Make sure you are in a quiet environment.",
  "Repeat at least 3-6 times.",
  "We are almost done collecting your cough sample.",
  "We are almost done collecting your cough sample.",
  "We are done collecting your cough sample.",
];

const CoughAudiSample = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState(false);
  const [title, setTitle] = useState("Cough Test");
  const [description, setDescription] = useState(
    "Find a noise-free location.\nTake a breath and cough.\nRepeat at least 3-6 times."
  );
  const [modalStep, setModalStep] = useState("idle"); // 'idle' | 'recording' | 'done'
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState("");
  const mediaStream = useRef(null);
  const mediaRecorder = useRef(null);
  const startingTimeStamp = useRef(0);
  const audioRecordB64Data = useRef("");

  useEffect(() => {
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: { exact: 1 },
            sampleSize: { exact: 16 },
            sampleRate: { ideal: 44100, min: 16000, max: 48000 },
            echoCancellation: { ideal: true },
            noiseSuppression: { ideal: false },
          },
        });
        mediaStream.current = stream;
        setPermissions(true);
      } catch (err) {
        console.error("Audio permission error:", err);
        setPermissions(false);
        setError("Microphone access denied.");
      }
    })();

    return () => {
      if (mediaStream.current) {
        mediaStream.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const isAudioValid = (base64Data) => {
    return new Promise((resolve) => {
      const audio = new Audio();
      audio.src = base64Data;
      audio.addEventListener("loadedmetadata", () => {
        console.log("Audio Duration:", audio.duration);
        resolve(audio.duration > 1);
      });
      audio.addEventListener("error", () => {
        resolve(false);
      });
    });
  };

  const startCapturingAudioStream = async () => {
    try {
      audioRecordB64Data.current = "";
      const { sampleRate } = mediaStream.current
        .getAudioTracks()[0]
        .getSettings();
      mediaRecorder.current = RecordRTC(mediaStream.current, {
        type: "audio",
        mimeType: "audio/wav",
        recorderType: RecordRTC.StereoAudioRecorder,
        numberOfAudioChannels: 1,
        sampleRate: sampleRate,
        desiredSampRate: 44100,
        timeSlice: 1000,
        ondataavailable: handleAudioDataAvailable,
      });
      mediaRecorder.current.setRecordingDuration(
        10000,
        handleAudioDataCollectionEnd
      );
      await mediaRecorder.current.startRecording();
      startingTimeStamp.current = performance.now();

      setModalStep("recording");
      setTitle("10 seconds left");
      setDescription(MessageList[0]);
    } catch (err) {
      console.error(err);
      setModalStep("idle");
      setError("Failed to Initiate the Cough Sample Capture.");
    }
  };

  const handleAudioDataAvailable = () => {
    const timeDiff = Math.round(
      (performance.now() - startingTimeStamp.current) / 1000
    );
    setTitle(`${10 - timeDiff} seconds left`);
    setDescription(MessageList[Math.floor(timeDiff / 2)] || "");
  };

  const handleAudioDataCollectionEnd = async () => {
    try {
      audioRecordB64Data.current = await new Promise((res) =>
        mediaRecorder.current.getDataURL(res)
      );
      setModalStep("done");
      setTitle("Sample Collected");
      setDescription("Submit your sample or re-record a new sample.");

      await mediaRecorder.current.destroy();
      mediaRecorder.current = null;
      startingTimeStamp.current = 0;
    } catch (err) {
      console.error("Collection failed", err);
      setError("Failed to capture the cough sample.");
    }
  };

  const submitAudioSample = async () => {
    try {
      const isValid = await isAudioValid(audioRecordB64Data.current);
      if (!isValid) {
        setError("Recorded audio is too short or blank. Please try again.");
        return;
      }
      setAnalyzing(true);
      const urlResp = await fetch(sdkURL("/pulmo/get-audio-url"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          api_key,
          employee_id: authEmployeeID,
          scan_id: state?.scanResult?.scan_id,
        }),
      });
      const urlRespJSON = await urlResp.json();
      if (urlRespJSON?.statusCode?.toString().startsWith("2")) {
        const aVowelFormdata = new FormData();
        const aVowelFormdataFields = Object.entries({
          ...(urlRespJSON.presigned_urls?.["vowel-a"]?.fields ?? {}),
          file: base64ToBlob(state?.aVowelAudioB64Data, "audio/wav"),
        });
        for (const [key, value] of aVowelFormdataFields)
          aVowelFormdata.append(key, value);
        const aVowelResp = await fetch(
          urlRespJSON.presigned_urls?.["vowel-a"]?.url,
          {
            method: "POST",
            body: aVowelFormdata,
          }
        );
        if (!aVowelResp.ok)
          throw new Error("Upload Failed for A-Vowel Audio Sample.");
        const coughFormdata = new FormData();
        const coughFormdataFields = Object.entries({
          ...(urlRespJSON.presigned_urls?.["cough"]?.fields ?? {}),
          file: base64ToBlob(audioRecordB64Data.current, "audio/wav"),
        });
        for (const [key, value] of coughFormdataFields)
          coughFormdata.append(key, value);
        const coughResp = await fetch(
          urlRespJSON.presigned_urls?.["cough"]?.url,
          {
            method: "POST",
            body: coughFormdata,
          }
        );
        if (!coughResp.ok)
          throw new Error("Upload Failed for Cough Audio Sample.");
        navigate("/pulmo-survey", {
          state: { ...state, coughAudioB64Data: audioRecordB64Data.current },
          replace: true,
        });
      } else throw new Error(urlRespJSON?.message ?? "Failed to fetch URLs.");
    } catch (err) {
      console.error(err);
      setAnalyzing(false);
      setError("Failed to Analyze the Cough Sample.");
    }
  };

  return (
    <section className="pt-2 pb-14 flex flex-col">
      {/* Top Bar */}
      <div className="px-6 tab-screen">
        <div className="flex items-center mb-8 w-full relative z-50">
          <Link
            to={-1}
            className="w-9 h-9 bg-[#2D2D2D] rounded-full flex flex-row justify-center items-center"
          >
            <img src={BackArrow} alt="" />
          </Link>
          <h2 className="text-xl lg:text-2xl md:text-2xl font-medium text-white ml-6">
            {title}
          </h2>
          <div></div>
        </div>
      </div>

      {/* Background Image */}
      <div className="-mt-20 relative">
        <div className="circle-blur lg:w-96 lg:h-96 md:w-96 md:h-96 w-80 h-80 mx-auto mt-14 blur-[70px] absolute left-0 right-0" />
        <img
          src={LunghImg}
          className="lg:w-6/12 md:w-6/12 w-full lg:rounded-full md:rounded-full mx-auto opacity-40"
          alt=""
        />
        <div className="absolute left-0 right-0 text-center w-full flex flex-row justify-center items-center top-10 h-52">
          {modalStep === "recording" ? (
            <div className="relative w-[155px] h-[155px] flex flex-row justify-center items-center">
              <img src={VoiceImg} alt="Voice Icon" className="absolute w-14" />
              <GlowingHeart width="155" height="155" />
            </div>
          ) : (
            <>
              <img src={VoiceImg} alt="Voice Icon" />
            </>
          )}
        </div>
      </div>

      {/* Foreground Card */}
      <div className="px-6 w-11/12 mx-auto -mt-28 relative z-50 lg:w-[80%] md:w-[80%]">
        <div className="bg-white rounded-[20px] lg:h-[340px] md:h-[340px] px-5 py-10 lg:py-5 md:py-5 text-center">
          <h2 className="text-lg font-semibold text-secondary mb-4">
            Cough Test
          </h2>

          {modalStep === "idle" && (
            <>
              <p className="text-sm font-light text-primary text-center mb-8 leading-4">
                Find a noise free location. Take a breath and cough. Repeat at
                least 3-6 times.
              </p>
              <div className="audio-player relative w-48 h-9 mx-auto mb-10">
                <p className="text-sm font-light text-white absolute left-[60px] top-2">
                  Sample Audio
                </p>
                <AudioPlayer
                  src="/audios/cough-sample.mp3"
                  onPlay={() => console.log("Sample Playing")}
                />
              </div>
              <div className="flex justify-center space-x-4 items-center">
                <button
                  onClick={() => {
                    navigate(-1);
                  }}
                  className="w-[155px] rounded-full outline-none px-5 h-9 mt-4 bg-white border border-black text-black text-sm font-medium flex justify-center items-center"
                >
                  Cancel
                </button>
                <button
                  onClick={startCapturingAudioStream}
                  className="w-[155px] rounded-full outline-none px-5 h-9 mt-2 border border-primary bg-black text-white text-sm font-medium flex justify-center items-center"
                >
                  Proceed
                </button>
              </div>
            </>
          )}

          {modalStep === "recording" && (
            <>
              <p className="text-[11px] text-black font-light mb-10">
                {description}
              </p>
              <p className="text-lg text-secondary font-bold mb-2">
                Recording...
              </p>
              <p className="text-xs text-gray-700 mb-20">
                Hold the phone closer to your mouth.
              </p>
              <button
                className="w-[155px] mx-auto rounded-full outline-none px-5 h-9 bg-white border border-black text-black text-sm font-medium flex justify-center items-center"
                disabled={
                  !permissions || modalStep === "recording" || analyzing
                }
                onClick={() => {
                  setModalStep("idle");
                }}
              >
                Cancel
              </button>
            </>
          )}

          {modalStep === "done" && (
            <>
              <p className="text-sm font-light text-black mb-3">
                Submit your sample or
                <br /> re-record a new sample
              </p>
              <p className="text-base text-secondary font-bold mb-2">
                Sample Collected
              </p>

              <div className="audio-player relative w-56 h-9 mx-auto mb-10 text-center">
                <p className="text-sm font-light text-white absolute left-[70px] top-2">
                  Recorded Audio
                </p>
                <AudioPlayer
                  src={audioRecordB64Data.current}
                  onPlay={() => console.log("Recorded Audio Playing")}
                />
              </div>
              <div className="flex flex-col justify-center space-x-4 items-center">
                <button
                  onClick={submitAudioSample}
                  disabled={analyzing}
                  className="lg:w-[220px] md:w-[220px] w-auto rounded-full outline-none px-5 h-9 mt-2 bg-black text-white tetx-sm font-medium flex justify-center items-center"
                >
                  Submit Recording
                  {analyzing && <Loader1 className="shrink-0 h-3 w-3 ml-2" />}
                </button>
                <button
                  className="lg:w-[210px] md:w-[210px] w-full rounded-full outline-none px-5 h-9 mt-3 bg-white border border-black text-black flex text-sm font-medium justify-center items-center"
                  onClick={startCapturingAudioStream}
                >
                  Re-collect New Sample
                </button>
              </div>
            </>
          )}

          {error.length > 0 && (
            <div className="fixed top-0 bottom-0 left-0 right-0 px-8 py-12 flex flex-col items-center justify-center bg-primary text-center">
              <img className="shrink-0 h-48" src={Failure} alt="Failure..." />
              <p className="mt-8 text-secondary text-xl font-bold">
                Sorry, we failed to record
                <br />
                your cough sample
              </p>
              <p className="mt-4 max-h-40 overflow-auto whitespace-pre-line text-white font-light">
                {error}
              </p>
              <button
                className="mt-8 w-60 px-5 py-2.5 rounded-full bg-white text-primary font-medium"
                type="button"
                onClick={() => {
                  navigate(-1);
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="w-full tab-screen px-6 lg:px-0 md:px-0">
        <div className="mt-4 p-5 rounded-[20px] bg-[#364560] text-white space-y-1.5">
          <h3 className="text-sm font-medium text-secondary">Disclaimer</h3>
          <p className="text-xxs text-justify font-light leading-4 text-white/80">
            For Investigational Use Only. Careplix is not a substitute for the
            clinical judgment of a health care professional. Careplix is
            intended to improve your awareness of general wellness. Careplix
            does not diagnose, treat, mitigate or prevent any disease, symptom,
            disorder or abnormal physical state. Consult with a health care
            professional or emergency services if you believe you may have a
            medical issue.
          </p>
        </div>
      </div>
    </section>
  );
};

export default CoughAudiSample;
